'use client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { X, AlertTriangle, Plus } from 'lucide-react';
import Button from '@/components/ui/Button';
import { FORMAT, MAX_MODELS_PER_TONE } from '@/utils/constants';
import { TonesSearchable } from '../SearchInput/ToneSearchable';
import { Option } from '../Select/Searchable';
import { useRouter } from 'next/navigation';
import { useToneModelCount } from '@/utils/models/useToneModelCount';
import { analytics } from '@/lib/analytics';
import { toast } from '@/components/ui/Toasts/use-toast';
interface Props {
  trigger: ReactNode;
  disabled?: boolean;
  modelId: number;
  toneId: number;
  userId: string;
  platform: FORMAT;
}

export const MoveModelModal = ({
  trigger,
  disabled = false,
  platform,
  modelId,
  toneId,
  userId,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const [selectedTone, setSelectedTone] = useState<Option | undefined>(undefined);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const toggleModal = () => {
    setIsOpen(!isOpen);
    setSelectedTone(undefined);
    setError(null);
  };

  const { count: destinationCount } = useToneModelCount(selectedTone?.value);
  const destinationFull = !!selectedTone && destinationCount >= MAX_MODELS_PER_TONE;

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onCreateNewPack = () => {
    analytics.capture('model.move.create_pack', { from: 'move_model_modal' });
    router.push(`/create/upload?page=tone&model=${modelId}`);
  };

  const onMove = async () => {
    if (!selectedTone) return;
    if (isMoving) return;
    if (!modelId) return;
    if (destinationFull) return;
    analytics.capture('model.moved');
    setIsMoving(true);
    setError(null);
    const res = await fetch('/api/models/move', {
      method: 'POST',
      body: JSON.stringify({ modelId, toneId: selectedTone.value }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (res.ok) {
      const { tone, model } = await res.json();
      toast({
        title: 'Model moved',
        description: `"${model.name}" has been moved to "${tone.title}"`,
      });
      router.refresh();
      toggleModal();
    } else {
      const data = await res.json().catch(() => ({}));
      setError(data?.error || 'An error occurred while trying to move the model');
    }
    setIsMoving(false);
  };

  return (
    <>
      <button
        disabled={disabled}
        onClick={toggleModal}
        className={`text-zinc-500 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:text-white'}`}
      >
        {trigger}
      </button>
      {isOpen && (
        <span className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
          <span
            ref={modalRef}
            className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-md rounded-xl shadow-xl flex flex-col"
          >
            <span className="flex justify-between items-center p-4 border-zinc-700">
              <span className="text-xl font-semibold">Move Model</span>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </span>
            <span className="p-4 flex flex-col gap-8">
              <span className="flex flex-col gap-3">
                <TonesSearchable
                  onChange={(option) => {
                    setSelectedTone(option);
                    setError(null);
                  }}
                  userId={userId}
                  platform={platform}
                  defaultTone={undefined}
                  excludeToneId={toneId}
                />
                <button
                  type="button"
                  onClick={onCreateNewPack}
                  className="flex items-center gap-2 self-start text-[#40A6FF] text-sm"
                >
                  <Plus size={16} />
                  Create new pack
                </button>
              </span>
              {destinationFull && (
                <span className="flex gap-3 items-start p-4 rounded-md border border-[#FF0000] bg-red-950/20">
                  <AlertTriangle size={20} className="text-[#FF0000] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-zinc-200">
                    This tone has reached the {MAX_MODELS_PER_TONE}-model limit. Pick a different tone.
                  </p>
                </span>
              )}
              {error && !destinationFull && (
                <span className="flex gap-3 items-start p-4 rounded-md border border-[#FF0000] bg-red-950/20">
                  <AlertTriangle size={20} className="text-[#FF0000] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-zinc-200">{error}</p>
                </span>
              )}
              <span className={'flex flex-row gap-4'}>
                <Button
                  className={'w-full'}
                  onClick={toggleModal}
                  isSecondary={true}
                  variant={'slim'}
                >
                  Cancel
                </Button>
                <Button
                  className={`w-full`}
                  onClick={onMove}
                  isPrimary={true}
                  variant={'slim'}
                  style={{
                    background: 'linear-gradient(180deg, #00f 0%, #0000d0 100%)',
                    filter: 'none',
                    opacity: !selectedTone || destinationFull ? 0.5 : 1,
                    color: 'white',
                  }}
                  disabled={!selectedTone || destinationFull}
                  loading={isMoving}
                >
                  Move
                </Button>
              </span>
            </span>
          </span>
        </span>
      )}
    </>
  );
};
